import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tooltip,
  Avatar,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Group as GroupIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Telegram as TelegramIcon,
  Sms as SmsIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Campaign } from '../../types';
import axios from '../../config/axios';

interface DisparoCampaign extends Campaign {
  leads: any[];
  message: string;
  channel: string;
  crmStage: string;
  leads_count?: number;
  stats?: {
    total: number;
    sent: number;
    delivered: number;
    failed: number;
    pending: number;
    retry: number;
  };
  settings: {
    enablePersonalization: boolean;
    enableTracking: boolean;
    enableRetry: boolean;
    maxRetries: number;
    delayBetweenMessages: number;
    sendInBatches: boolean;
    batchSize: number;
    batchDelay: number;
  };
}

const DisparosList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [campaigns, setCampaigns] = useState<DisparoCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<DisparoCampaign | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<DisparoCampaign | null>(null);

  // Carregar campanhas de disparo
  useEffect(() => {
    fetchDisparoCampaigns();
  }, []);

  const fetchDisparoCampaigns = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/disparos');
      const campaignsData = response.data.data || [];
      
      // Buscar estatísticas para cada campanha
      const campaignsWithStats = await Promise.all(
        campaignsData.map(async (campaign: DisparoCampaign) => {
          try {
            const statsResponse = await axios.get(`/api/v1/disparos/${campaign.id}/stats`);
            return {
              ...campaign,
              stats: statsResponse.data.data
            };
          } catch (error) {
            console.error(`Erro ao buscar estatísticas da campanha ${campaign.id}:`, error);
            return {
              ...campaign,
              stats: { total: 0, sent: 0, delivered: 0, failed: 0, pending: 0, retry: 0 }
            };
          }
        })
      );
      
      setCampaigns(campaignsWithStats);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    navigate('/campaigns/disparos/create');
  };

  const handleEditCampaign = (campaign: DisparoCampaign) => {
    setSelectedCampaign(campaign);
    setEditDialogOpen(true);
  };

  const handleDeleteCampaign = (campaign: DisparoCampaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    try {
      await axios.delete(`/api/v1/disparos/${campaignToDelete.id}`);
      setCampaigns(campaigns.filter(c => c.id !== campaignToDelete.id));
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    } catch (error) {
      console.error('Erro ao deletar campanha:', error);
    }
  };

  const handleActivateCampaign = async (campaign: DisparoCampaign) => {
    try {
      // Mostrar feedback de loading
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'activating' as any }
          : c
      ));

      // Aumentar timeout para 5 minutos (300 segundos) para processamento massivo
      await axios.post(`/api/v1/disparos/${campaign.id}/start`, {}, {
        timeout: 300000, // 5 minutos
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Atualizar status da campanha
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'active' as any }
          : c
      ));
      
      // Recarregar estatísticas após ativação
      setTimeout(() => {
        fetchDisparoCampaigns();
      }, 3000);
      
      console.log('✅ Campanha ativada com sucesso');
      
    } catch (error: any) {
      console.error('Erro ao ativar campanha:', error);
      
      // Reverter status em caso de erro
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'draft' as any }
          : c
      ));
      
      if (error.code === 'ECONNABORTED') {
        console.error('⏰ Timeout: Campanha está sendo processada em background');
        alert('A campanha está sendo ativada em background. Isso pode levar alguns minutos para campanhas com muitos leads. Verifique o status em alguns minutos.');
      } else {
        alert(`Erro ao ativar campanha: ${error.message}`);
      }
    }
  };

  const handleReactivateCampaign = async (campaign: DisparoCampaign) => {
    try {
      // Mostrar feedback de loading
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'activating' as any }
          : c
      ));

      // Aumentar timeout para 5 minutos para processamento massivo
      await axios.post(`/api/v1/disparos/${campaign.id}/start`, {}, {
        timeout: 300000, // 5 minutos
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Atualizar status da campanha
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'active' as any }
          : c
      ));
      
      // Recarregar estatísticas após reativação
      setTimeout(() => {
        fetchDisparoCampaigns();
      }, 3000);
      
      console.log('✅ Campanha reativada com sucesso');
      
    } catch (error: any) {
      console.error('Erro ao reativar campanha:', error);
      
      // Reverter status em caso de erro
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'paused' as any }
          : c
      ));
      
      if (error.code === 'ECONNABORTED') {
        console.error('⏰ Timeout: Campanha está sendo reativada em background');
        alert('A campanha está sendo reativada em background. Isso pode levar alguns minutos para campanhas com muitos leads. Verifique o status em alguns minutos.');
      } else {
        alert(`Erro ao reativar campanha: ${error.message}`);
      }
    }
  };

  const handlePauseCampaign = async (campaign: DisparoCampaign) => {
    try {
      setActionLoading(campaign.id);
      await axios.post(`/api/v1/disparos/${campaign.id}/pause`);
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'paused' as any }
          : c
      ));
    } catch (error) {
      console.error('Erro ao pausar campanha:', error);
      alert('Erro ao pausar campanha. Tente novamente.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopCampaign = async (campaign: DisparoCampaign) => {
    try {
      setActionLoading(campaign.id);
      await axios.post(`/api/v1/disparos/${campaign.id}/stop`);
      setCampaigns(campaigns.map(c => 
        c.id === campaign.id 
          ? { ...c, status: 'completed' as any }
          : c
      ));
    } catch (error) {
      console.error('Erro ao parar campanha:', error);
      alert('Erro ao parar campanha. Tente novamente.');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'active':
        return 'info';
      case 'running':
        return 'info';
      case 'activating':
        return 'warning';
      case 'scheduled':
        return 'warning';
      case 'draft':
        return 'default';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Concluída';
      case 'active':
        return 'Ativa';
      case 'running':
        return 'Executando';
      case 'activating':
        return 'Ativando...';
      case 'scheduled':
        return 'Agendada';
      case 'draft':
        return 'Rascunho';
      case 'failed':
        return 'Falhou';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return <WhatsAppIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'telegram':
        return <TelegramIcon />;
      case 'sms':
        return <SmsIcon />;
      default:
        return <SendIcon />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return '#25D366';
      case 'instagram':
        return '#E4405F';
      case 'telegram':
        return '#0088CC';
      case 'sms':
        return '#FF6B35';
      default:
        return '#666';
    }
  };

  // Calcular estatísticas gerais
  const totalSent = campaigns.reduce((acc, c) => acc + (c.stats?.sent || 0), 0);
  const totalDelivered = campaigns.reduce((acc, c) => acc + (c.stats?.delivered || 0), 0);
  const totalFailed = campaigns.reduce((acc, c) => acc + (c.stats?.failed || 0), 0);
  const deliveryRate = totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0;

  if (loading) {
    return (
      <Box sx={{ 
        p: { xs: 2, md: 3 }, 
        backgroundColor: 'transparent', 
        minHeight: '100vh',
        color: 'text.primary',
      }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, color: '#ffffff' }}>
          Carregando campanhas...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Header */}
      <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#000000',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 0.5,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Campanhas de Disparo
          </Typography>
          <Typography variant="body2" sx={{ 
            color: '#64748b',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Gerencie suas campanhas de disparo em massa
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', md: 'auto' }
        }}>
          <Tooltip title="Atualizar dados">
            <IconButton 
              onClick={fetchDisparoCampaigns} 
              disabled={loading}
              sx={{ color: '#3b82f6', '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.1)' } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCampaign}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              }
            }}
          >
            Nova Campanha
          </Button>
        </Box>
      </Box>

      {/* Métricas - Estilo Tailwind Template */}
      <div className="grid grid-cols-12 gap-6 mb-8">
        {/* 1. Total de Campanhas */}
        <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-3 glass-blue-card rounded-xl cursor-pointer relative overflow-hidden">
          <div className="px-5 pt-5">
            <div className="flex justify-between items-start mb-2">
              <div className="p-3 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <GroupIcon className="text-blue-600" style={{ fontSize: 32 }} />
              </div>
              <Chip 
                label="Total" 
                size="small" 
                className="bg-blue-500/10 text-blue-600 font-semibold border border-blue-500/30"
              />
            </div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Total de Campanhas</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-900 mr-2">
                {campaigns.length.toLocaleString()}
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              Campanhas criadas
            </div>
          </div>
        </div>

        {/* 2. Mensagens Enviadas */}
        <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-3 glass-blue-card rounded-xl cursor-pointer relative overflow-hidden">
          <div className="px-5 pt-5">
            <div className="flex justify-between items-start mb-2">
              <div className="p-3 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <SendIcon className="text-blue-600" style={{ fontSize: 32 }} />
              </div>
              <Chip 
                label="Enviadas" 
                size="small" 
                className="bg-blue-500/10 text-blue-600 font-semibold border border-blue-500/30"
              />
            </div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Mensagens Enviadas</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-900 mr-2">
                {totalSent.toLocaleString()}
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              Total enviado
            </div>
          </div>
        </div>

        {/* 3. Taxa de Entrega */}
        <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-3 glass-blue-card rounded-xl cursor-pointer relative overflow-hidden">
          <div className="px-5 pt-5">
            <div className="flex justify-between items-start mb-2">
              <div className="p-3 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ScheduleIcon className="text-blue-600" style={{ fontSize: 32 }} />
              </div>
              <Chip 
                icon={<StarIcon style={{ fontSize: 14 }} />}
                label="Taxa" 
                size="small" 
                className="bg-blue-500/10 text-blue-600 font-semibold border border-blue-500/30"
              />
            </div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Taxa de Entrega</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-900 mr-2">
                {deliveryRate}%
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              Taxa de sucesso
            </div>
          </div>
        </div>

        {/* 4. Falhas */}
        <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-3 glass-blue-card rounded-xl cursor-pointer relative overflow-hidden">
          <div className="px-5 pt-5">
            <div className="flex justify-between items-start mb-2">
              <div className="p-3 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <StopIcon className="text-blue-600" style={{ fontSize: 32 }} />
              </div>
              <Chip 
                icon={<WarningIcon style={{ fontSize: 14 }} />}
                label="Falhas" 
                size="small"
                className="bg-blue-500/10 text-blue-600 font-semibold border border-blue-500/30"
              />
            </div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Falhas</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-900 mr-2">
                {totalFailed.toLocaleString()}
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              Mensagens falhadas
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Campanhas */}
      <div className="glass-blue-card rounded-xl mb-8">
        <div className="px-5 pt-5">
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            mb: 2,
            color: '#000000',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Campanhas de Disparo
          </Typography>
          
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          {campaigns.length === 0 && !loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SendIcon sx={{ fontSize: 60, color: '#64748b', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: '#000000' }}>
                Nenhuma campanha encontrada
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                Crie sua primeira campanha de disparo em massa
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateCampaign}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                  }
                }}
              >
                Criar Primeira Campanha
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Campanha
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Canal
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Destinatários
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Enviadas
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Entregues
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Taxa de Entrega
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: '#000000',
                      fontWeight: 600,
                      backgroundColor: 'rgba(59, 130, 246, 0.08)',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => {
                    const stats = campaign.stats || { total: 0, sent: 0, delivered: 0, failed: 0, pending: 0, retry: 0 };
                    const deliveryRate = stats.sent > 0 ? Math.round((stats.delivered / stats.sent) * 100) : 0;
                    
                    return (
                      <TableRow 
                        key={campaign.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.05)',
                          },
                          borderBottom: '1px solid rgba(59, 130, 246, 0.1)'
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#000000' }}>
                              {campaign.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {campaign.description}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>
                              Criada em {new Date(campaign.created_at).toLocaleDateString('pt-BR')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                bgcolor: getChannelColor(campaign.channel),
                                width: 24,
                                height: 24,
                              }}
                            >
                              {getChannelIcon(campaign.channel)}
                            </Avatar>
                            <Typography variant="body2" sx={{ color: '#475569' }}>
                              {campaign.channel}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(campaign.status)}
                            color={getStatusColor(campaign.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: '#475569' }}>
                            {campaign.leads_count || stats.total || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: '#475569' }}>
                            {stats.sent}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: '#475569' }}>
                            {stats.delivered}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: '#475569' }}>
                            {deliveryRate}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleEditCampaign(campaign)}
                                sx={{ color: '#3b82f6', '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.1)' } }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            
                            {(campaign.status === 'draft' || campaign.status === 'activating') && (
                              <Tooltip title={campaign.status === 'activating' ? 'Ativando campanha...' : 'Ativar'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={campaign.status === 'activating'}
                                    onClick={() => handleActivateCampaign(campaign)}
                                    sx={{ color: '#10b981', '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.1)' } }}
                                  >
                                    {campaign.status === 'activating' ? <CircularProgress size={16} /> : <PlayIcon />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            
                            {campaign.status === 'active' && (
                              <Tooltip title="Pausar">
                                <IconButton
                                  size="small"
                                  disabled={actionLoading === campaign.id}
                                  onClick={() => handlePauseCampaign(campaign)}
                                  sx={{ color: '#f59e0b', '&:hover': { backgroundColor: 'rgba(245, 158, 11, 0.1)' } }}
                                >
                                  {actionLoading === campaign.id ? <CircularProgress size={16} /> : <PauseIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {(campaign.status === 'active' || campaign.status === 'running') && (
                              <Tooltip title="Parar">
                                <IconButton
                                  size="small"
                                  disabled={actionLoading === campaign.id}
                                  onClick={() => handleStopCampaign(campaign)}
                                  sx={{ color: '#ef4444', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' } }}
                                >
                                  {actionLoading === campaign.id ? <CircularProgress size={16} /> : <StopIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {(campaign.status === 'completed' || campaign.status === 'failed') && (
                              <Tooltip title="Reativar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleReactivateCampaign(campaign)}
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                >
                                  <RefreshIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            <Tooltip title="Deletar">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteCampaign(campaign)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>

      {/* Dialog de Edição */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: 'glass-blue-card',
          sx: {
            '&:hover': { background: 'inherit', borderColor: 'inherit', boxShadow: 'inherit', transform: 'none' }
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#000000',
          fontWeight: 600,
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          pb: 2
        }}>
          Detalhes da Campanha
        </DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#000000' }}>
                {selectedCampaign.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                {selectedCampaign.description}
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box className="glass-blue-card rounded-lg p-3">
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
                    Canal
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#000000' }}>
                    {selectedCampaign.channel}
                  </Typography>
                </Box>
                <Box className="glass-blue-card rounded-lg p-3">
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
                    Status
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedCampaign.status)}
                    color={getStatusColor(selectedCampaign.status) as any}
                    size="small"
                  />
                </Box>
                <Box className="glass-blue-card rounded-lg p-3">
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
                    Destinatários
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#000000' }}>
                    {selectedCampaign.leads_count || selectedCampaign.stats?.total || 0}
                  </Typography>
                </Box>
                <Box className="glass-blue-card rounded-lg p-3">
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
                    Mensagens Enviadas
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#000000' }}>
                    {selectedCampaign.stats?.sent || 0}
                  </Typography>
                </Box>
                <Box className="glass-blue-card rounded-lg p-3">
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
                    Mensagens Entregues
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#000000' }}>
                    {selectedCampaign.stats?.delivered || 0}
                  </Typography>
                </Box>
                <Box className="glass-blue-card rounded-lg p-3">
                  <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
                    Taxa de Entrega
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#000000' }}>
                    {selectedCampaign.stats?.sent && selectedCampaign.stats.sent > 0 
                      ? `${Math.round((selectedCampaign.stats.delivered || 0) / selectedCampaign.stats.sent * 100)}%`
                      : '0%'
                    }
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b' }}>
                Mensagem
              </Typography>
              <Box className="glass-blue-card rounded-lg p-3">
                <Typography variant="body2" sx={{ color: '#000000' }}>
                  {selectedCampaign.message}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: '#64748b' }}
          >
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setEditDialogOpen(false);
              if (selectedCampaign) {
                navigate(`/campaigns/disparos/edit/${selectedCampaign.id}`);
              }
            }}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              }
            }}
          >
            Editar Campanha
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Deletar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          className: 'glass-blue-card',
          sx: {
            '&:hover': { background: 'inherit', borderColor: 'inherit', boxShadow: 'inherit', transform: 'none' }
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#000000',
          fontWeight: 600,
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          pb: 2
        }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ color: '#000000' }}>
            Tem certeza que deseja excluir a campanha "{campaignToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: '#64748b' }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteCampaign}
            sx={{
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
              }
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisparosList; 